import { captureException } from '@sentry/browser';
import { useLazyQuery } from '@vue/apollo-composable';
import DUMMY from 'gql/dummy.gql';
import parseNumber from 'utils/parseNumber';

import type { FischerPlugin } from '@/lib/fischerPlugin';

import { useEventHub } from './eventHub';
import { useFischer } from './fischerPlugin';

// @ts-ignore
const GET_BESTPRICE = () => import('gql/getBestPrice.gql');

export type TrackingEventItem = {
  currency?: string;
  item_name?: string;
  item_id?: string;
  item_brand?: string;
  item_list_name?: string;
  item_list_id?: string;
  index?: number;
  item_variant?: string;
  price?: number;
  productPrice?: number;
  productId?: string;
  quantity?: number;
  cart_id?: number | string;
};

export const isShopFunction = (fischer: FischerPlugin): boolean =>
  fischer.shop.isClosedShop() || fischer.shop.isOnlineShop();

export const enableTrackingFunction = (fischer: FischerPlugin): boolean =>
  fischer.enableAnalytics();

export const enableTrackingShopFunction = (fischer: FischerPlugin): boolean =>
  isShopFunction(fischer) || fischer.shop.isRetailConnectCart();

export const getPriceForTrackingFunction = async (
  productIdUnpadded: string,
  qty: number = 0,
  fischer: FischerPlugin,
  getBestPriceQuery: (variables: {
    baseSiteId: string;
    accessToken: string;
    productCode: string;
    qty: number;
    language: string;
    expired: boolean;
    userId: string;
  }) => Promise<{ data?: any } | undefined>,
): Promise<{
  price: number | undefined;
  quantity: number;
  currency: string;
  totalPrice: number | undefined;
}> => {
  if (isShopFunction(fischer) && productIdUnpadded) {
    try {
      const productId = productIdUnpadded.padStart(6, '0');
      const result = await getBestPriceQuery({
        baseSiteId: fischer.baseSiteId(),
        accessToken: fischer.user.accessToken(),
        productCode: productId,
        qty: qty,
        language: fischer.siteLanguage(),
        expired: true,
        userId: fischer.user.id(),
      });

      const bestPrice = result?.data?.getBestPrice;
      if (
        bestPrice &&
        bestPrice.price?.valueFormatted &&
        bestPrice.price?.totalValueFormatted &&
        bestPrice.initialQuantity
      ) {
        return {
          currency: bestPrice.price.currencyIso,
          price: parseNumber(bestPrice.price.valueFormatted) / bestPrice.initialQuantity,
          quantity: bestPrice.initialQuantity || '1',
          totalPrice: parseNumber(bestPrice.price.totalValueFormatted),
        };
      }

      return { price: undefined, quantity: qty, currency: '', totalPrice: undefined };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.trace('error getting best price', error, productIdUnpadded, qty);
    }
  }

  return { price: undefined, quantity: qty, currency: '', totalPrice: undefined };
};

export const trackAddMultipleToCartFunction = async (
  cartId: number | string,
  productsWithQuantity: {
    id: string;
    quantity: number;
  }[],
  fischer: FischerPlugin,
  getBestPriceQuery: Parameters<typeof getPriceForTrackingFunction>[3],
  sendEvent: (
    type: string,
    data: {
      cartId: string | number;
      items: TrackingEventItem[];
    },
  ) => Promise<void>,
) => {
  try {
    if (!(enableTrackingFunction(fischer) && enableTrackingShopFunction(fischer))) {
      return;
    }

    const itemsWithNull: (TrackingEventItem | null)[] = await Promise.all(
      productsWithQuantity.map(async ({ id: productId, quantity }: any) => {
        try {
          const item: TrackingEventItem = {
            productId,
            quantity,
          };
          const { totalPrice, currency } = await getPriceForTrackingFunction(
            productId,
            quantity,
            fischer,
            getBestPriceQuery,
          );
          if (totalPrice) {
            item.productPrice = totalPrice;
            item.currency = currency;
          }
          return item;
        } catch (error) {
          console.error('could not get info on product', productId, quantity, error);
          return null;
        }
      }),
    );
    const items = itemsWithNull.filter((item) => item) as TrackingEventItem[];

    if (items.length) {
      sendEvent('add_to_cart', {
        cartId,
        items,
      });
    }
  } catch (error) {
    console.error(error);
    captureException(error);
  }
};

export const useTracking = () => {
  const fischer = useFischer();
  const { sendEvent } = useEventHub();

  const enableTracking = () => enableTrackingFunction(fischer);

  const enableTrackingShop = () => enableTrackingShopFunction(fischer);

  const isShop = () => isShopFunction(fischer);

  type getPriceForTrackingParams = Parameters<typeof getPriceForTrackingFunction>;
  const { load, refetch } = useLazyQuery(DUMMY);
  const getPriceForTracking = async (
    productId: getPriceForTrackingParams[0],
    quantity: getPriceForTrackingParams[1],
  ) => {
    const query = (await GET_BESTPRICE()).default;
    return getPriceForTrackingFunction(
      productId,
      quantity,
      fischer,
      (variables) => load(query, variables) || refetch(variables),
    );
  };

  const trackAddMultipleToCart = async (
    cartId: number | string,
    productsWithQuantity: {
      id: string;
      quantity: number;
    }[],
  ) => {
    const query = (await GET_BESTPRICE()).default;
    return trackAddMultipleToCartFunction(
      cartId,
      productsWithQuantity,
      fischer,
      (variables) => load(query, variables) || refetch(variables),
      sendEvent,
    );
  };

  return {
    enableTracking,
    enableTrackingShop,
    isShop,
    getPriceForTracking,
    trackAddMultipleToCart,
  };
};
